// ═══════════════════════════════════════════════════
// Topbar — Property selector, notifications, user
// The control tower of your compliance ops
// ═══════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import {
  Bell,
  CaretDown,
  Buildings,
  MagnifyingGlass,
  User as UserIcon,
} from '@phosphor-icons/react';
import { useAuthStore } from '@/stores/auth';
import { useUIStore } from '@/stores/ui';
import styles from './Topbar.module.css';

// Fun motivational quips that rotate daily
const DAILY_QUIPS = [
  'Making compliance look easy since 2026.',
  'Your properties are counting on you. No pressure.',
  'Another day, another violation squashed.',
  'Consistency is the ultimate flex.',
  'Fair Housing never takes a day off. Neither do we.',
  'Your Pulse Score called. It wants to be higher.',
  'Scanning for excellence... and violations.',
  "Compliance isn't luck. It's Pulse.",
];

function getDailyQuip(): string {
  const dayOfYear = Math.floor(
    (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000,
  );
  return DAILY_QUIPS[dayOfYear % DAILY_QUIPS.length] ?? DAILY_QUIPS[0]!;
}

export function Topbar() {
  const user = useAuthStore((s) => s.user);
  const activeProperty = useAuthStore((s) => s.activeProperty);
  const unreadCount = useUIStore((s) => s.unreadCount);
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  // Close notification dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <header
      className={styles.topbar}
      style={{
        left: sidebarCollapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)',
      }}
    >
      {/* Left: Property Selector */}
      <div className={styles.left}>
        <button className={styles.propertySelector} aria-label="Select property">
          <span className={styles.propertySelectorIcon}>
            <Buildings size={18} weight="duotone" />
          </span>
          <span className={styles.propertySelectorText}>
            {activeProperty ? activeProperty.name : 'All Properties'}
          </span>
          <CaretDown size={14} className={styles.propertySelectorCaret} />
        </button>

        {/* Daily quip */}
        <span className={styles.quip}>{getDailyQuip()}</span>
      </div>

      {/* Right: Search, Notifications, User */}
      <div className={styles.right}>
        {/* Quick Search */}
        <button className={styles.iconBtn} aria-label="Search findings and properties">
          <MagnifyingGlass size={18} weight="regular" />
          <span className={styles.searchHint}>
            <kbd className={styles.kbd}>⌘</kbd>
            <kbd className={styles.kbd}>K</kbd>
          </span>
        </button>

        {/* Notifications */}
        <div className={styles.notifWrap} ref={notifRef}>
          <button
            className={styles.iconBtn}
            onClick={() => setNotifOpen(!notifOpen)}
            aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
          >
            <Bell size={18} weight={unreadCount > 0 ? 'fill' : 'regular'} />
            {unreadCount > 0 && (
              <span className={styles.badge} aria-hidden="true">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className={styles.notifDropdown} role="dialog" aria-label="Notifications">
              <div className={styles.notifHeader}>
                <h3>Notifications</h3>
                <button className={styles.notifMarkAll}>Mark all read</button>
              </div>
              <div className={styles.notifEmpty}>
                <Bell size={32} weight="duotone" className={styles.notifEmptyIcon} />
                <p>You're all caught up!</p>
                <span className={styles.notifEmptySub}>
                  No violations snuck past you today. Nice.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* User */}
        <button className={styles.userBtn} aria-label="User menu">
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className={styles.avatar} />
          ) : (
            <span className={styles.avatarFallback}>
              <UserIcon size={16} weight="bold" />
            </span>
          )}
        </button>
      </div>
    </header>
  );
}

